import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DollarSign,
  FileText,
  Download,
  Calendar,
  TrendingUp,
  Monitor,
  Play,
  Clock,
  CheckCircle2,
  AlertCircle,
  CreditCard,
  Receipt,
  Eye,
  RefreshCw,
  Building2,
} from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

export default function ClientBillingPage() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [currentBilling, setCurrentBilling] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  
  // For generating invoices (admin only)
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    client_id: '',
    month: format(subMonths(new Date(), 1), 'yyyy-MM'),
  });

  useEffect(() => {
    loadData();
  }, [selectedClient]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = selectedClient !== 'all' ? { client_id: selectedClient } : {};
      
      const requests = [
        api.get('/billing/invoices', { params }),
        api.get('/billing/current', { params }),
      ];
      
      if (isAdmin) {
        requests.push(api.get('/users'));
      }
      
      const [invoicesRes, currentRes, usersRes] = await Promise.all(requests);
      
      setInvoices(invoicesRes.data.invoices || []);
      setCurrentBilling(currentRes.data);
      
      if (usersRes) {
        setClients(usersRes.data.filter(u => u.role === 'client'));
      }
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!generateForm.client_id) {
      toast.error('Please select a client');
      return;
    }
    
    try {
      const res = await api.post('/billing/invoices/generate', {
        client_id: generateForm.client_id,
        billing_period: generateForm.month,
      });
      toast.success('Invoice generated successfully');
      setShowGenerateDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await api.put(`/billing/invoices/${invoiceId}/status`, { status: 'paid' });
      toast.success('Invoice marked as paid');
      loadData();
    } catch (error) {
      toast.error('Failed to update invoice status');
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    // Generate PDF-like content (in real app, this would be a proper PDF)
    const invoiceContent = `
INVOICE #${invoice.invoice_number}
=====================================

Client: ${invoice.client_name}
Company: ${invoice.client_company || 'N/A'}
Email: ${invoice.client_email}

Billing Period: ${format(new Date(invoice.billing_period_start), 'MMM d, yyyy')} - ${format(new Date(invoice.billing_period_end), 'MMM d, yyyy')}
Invoice Date: ${format(new Date(invoice.created_at), 'MMM d, yyyy')}
Due Date: ${format(new Date(invoice.due_date), 'MMM d, yyyy')}

=====================================
USAGE SUMMARY
=====================================

Total Ad Plays: ${invoice.total_plays?.toLocaleString() || 0}
Total Impressions: ${invoice.total_impressions?.toLocaleString() || 0}
Screens Used: ${invoice.screens_used || 0}

=====================================
LINE ITEMS
=====================================

${(invoice.line_items || []).map(item => 
  `${item.description}
   Quantity: ${item.quantity} x $${item.unit_price.toFixed(2)} = $${item.amount.toFixed(2)}`
).join('\n\n')}

=====================================
TOTAL: $${invoice.total_amount?.toFixed(2) || '0.00'}
=====================================

Status: ${invoice.status.toUpperCase()}

Thank you for your business!
    `.trim();
    
    const blob = new Blob([invoiceContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice_${invoice.invoice_number}.txt`;
    a.click();
    
    toast.success('Invoice downloaded');
  };

  const viewInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'pending':
        return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'overdue':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      default:
        return 'bg-zinc-700 text-zinc-400 border-zinc-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            {isAdmin ? 'Client Billing' : 'My Billing'}
          </h1>
          <p className="text-zinc-400 mt-1">
            {isAdmin ? 'Generate and manage client invoices' : 'View your invoices and billing history'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} className="border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {isAdmin && (
            <Button 
              onClick={() => setShowGenerateDialog(true)} 
              className="bg-blue-600 hover:bg-blue-500"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Admin: Client Filter */}
      {isAdmin && (
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Filter by Client</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-64">
                    <SelectValue placeholder="All Clients" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} {client.company && `(${client.company})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Period Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <DollarSign className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Current Month</p>
                <p className="text-white text-2xl font-bold">
                  ${currentBilling?.current_month_total?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Play className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Ad Plays (MTD)</p>
                <p className="text-white text-2xl font-bold">
                  {currentBilling?.total_plays?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Monitor className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Active Screens</p>
                <p className="text-white text-2xl font-bold">
                  {currentBilling?.screens_used || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Receipt className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Pending Invoices</p>
                <p className="text-white text-2xl font-bold">
                  {invoices.filter(i => i.status === 'pending').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" />
            Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Invoice #</TableHead>
                  {isAdmin && <TableHead className="text-zinc-500">Client</TableHead>}
                  <TableHead className="text-zinc-500">Period</TableHead>
                  <TableHead className="text-zinc-500">Amount</TableHead>
                  <TableHead className="text-zinc-500">Status</TableHead>
                  <TableHead className="text-zinc-500">Due Date</TableHead>
                  <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id} className="border-zinc-800">
                    <TableCell className="font-mono text-white">{invoice.invoice_number}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div>
                          <p className="text-white">{invoice.client_name}</p>
                          <p className="text-zinc-500 text-xs">{invoice.client_company}</p>
                        </div>
                      </TableCell>
                    )}
                    <TableCell className="text-zinc-400">
                      {format(new Date(invoice.billing_period_start), 'MMM yyyy')}
                    </TableCell>
                    <TableCell className="text-white font-medium">
                      ${invoice.total_amount?.toFixed(2) || '0.00'}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(invoice.status)}>
                        {invoice.status === 'paid' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                        {invoice.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                        {invoice.status === 'overdue' && <AlertCircle className="w-3 h-3 mr-1" />}
                        {invoice.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-400">
                      {format(new Date(invoice.due_date), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewInvoiceDetails(invoice)}
                          className="border-zinc-700"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadInvoice(invoice)}
                          className="border-zinc-700"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {isAdmin && invoice.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleMarkPaid(invoice.id)}
                            className="bg-emerald-600 hover:bg-emerald-500"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400">No invoices found</p>
              {isAdmin && (
                <p className="text-zinc-500 text-sm mt-1">Generate an invoice for a client to get started</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Invoice Dialog (Admin) */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Generate Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Client</Label>
              <Select
                value={generateForm.client_id}
                onValueChange={(value) => setGenerateForm({ ...generateForm, client_id: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} {client.company && `(${client.company})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Billing Period</Label>
              <Input
                type="month"
                value={generateForm.month}
                onChange={(e) => setGenerateForm({ ...generateForm, month: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <p className="text-zinc-500 text-sm">
              Invoice will be generated based on the client's screen usage and ad plays during this period.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleGenerateInvoice} className="bg-blue-600 hover:bg-blue-500">
              Generate Invoice
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Details Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Invoice #{selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6 py-4">
              {/* Invoice Header */}
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-zinc-400 text-sm">Bill To:</p>
                  <p className="text-white font-medium">{selectedInvoice.client_name}</p>
                  <p className="text-zinc-400">{selectedInvoice.client_company}</p>
                  <p className="text-zinc-500 text-sm">{selectedInvoice.client_email}</p>
                </div>
                <div className="text-right">
                  <Badge className={getStatusColor(selectedInvoice.status)}>
                    {selectedInvoice.status}
                  </Badge>
                  <p className="text-zinc-400 text-sm mt-2">
                    Invoice Date: {format(new Date(selectedInvoice.created_at), 'MMM d, yyyy')}
                  </p>
                  <p className="text-zinc-400 text-sm">
                    Due Date: {format(new Date(selectedInvoice.due_date), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>

              {/* Billing Period */}
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <p className="text-zinc-400 text-sm">Billing Period</p>
                <p className="text-white font-medium">
                  {format(new Date(selectedInvoice.billing_period_start), 'MMM d, yyyy')} - {format(new Date(selectedInvoice.billing_period_end), 'MMM d, yyyy')}
                </p>
              </div>

              {/* Usage Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{selectedInvoice.total_plays?.toLocaleString() || 0}</p>
                  <p className="text-zinc-500 text-sm">Ad Plays</p>
                </div>
                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{selectedInvoice.total_impressions?.toLocaleString() || 0}</p>
                  <p className="text-zinc-500 text-sm">Impressions</p>
                </div>
                <div className="p-4 bg-zinc-800/50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{selectedInvoice.screens_used || 0}</p>
                  <p className="text-zinc-500 text-sm">Screens</p>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <p className="text-zinc-400 text-sm mb-2">Line Items</p>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-700">
                      <TableHead className="text-zinc-500">Description</TableHead>
                      <TableHead className="text-zinc-500 text-right">Qty</TableHead>
                      <TableHead className="text-zinc-500 text-right">Unit Price</TableHead>
                      <TableHead className="text-zinc-500 text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedInvoice.line_items || []).map((item, idx) => (
                      <TableRow key={idx} className="border-zinc-700">
                        <TableCell className="text-white">{item.description}</TableCell>
                        <TableCell className="text-zinc-400 text-right">{item.quantity}</TableCell>
                        <TableCell className="text-zinc-400 text-right">${item.unit_price?.toFixed(2)}</TableCell>
                        <TableCell className="text-white text-right font-medium">${item.amount?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Total */}
              <div className="flex justify-end">
                <div className="p-4 bg-blue-500/10 rounded-lg">
                  <p className="text-zinc-400 text-sm">Total Amount</p>
                  <p className="text-3xl font-bold text-white">${selectedInvoice.total_amount?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowInvoiceDialog(false)} className="border-zinc-700">
              Close
            </Button>
            <Button onClick={() => handleDownloadInvoice(selectedInvoice)} className="bg-blue-600 hover:bg-blue-500">
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
